import React from "react";
import PrintReportHeader from "./PrintReportHeader";
import PageHeaderTitle from "../shared-components/PageHeaderTitle";
import { classNames } from "../utils";
import { Modal } from "../shared-components/Modal";
import { ChangeDueDateContainer } from "./ChangeDueDateContainer";
import ReportSidebar from "./ReportSidebar/ReportSidebar";
import AddNoteIcon from "../shared-components/icons/AddNoteIcon";
import ReportTimer from "./ReportTimer";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import SecurityLockIcon from "../shared-components/icons/SecurityLockIcon";
import TableReportDateWithFormat from "../shared-components/TableReportDateWithFormat";
import { ReportSummaryItemApi } from "../api/Reports";
import { SummaryFilterTypeEnum } from "./ReportSidebar/SidebarSummary";
import { FlagCountResponse, GroupCount, TopicCount } from "../api/Items";
import { BlueprintEx2 } from "../api/Blueprints";
import { ReportDetailTypes } from "./utils";
import { useFamaFlags } from "../api/FamaFlags";

type Props = {
  reportSummary: ReportSummaryItemApi;
  summaryOnly: boolean | undefined;
  getDetailTitle: () => string;
  useStatsInSummary: boolean;
  report: ReportSummaryItemApi | undefined;
  isReadOnly: boolean;
  reviewIsInProgress: () => boolean;
  canChangeDueDate: boolean;
  selectedDate: Date | undefined;
  handleDueDateChange: (newDueDate: Date) => Promise<void>;
  applyFilterByType: (
    type: SummaryFilterTypeEnum,
    event: React.ChangeEvent<HTMLInputElement> | null,
    source: string
  ) => void;
  flagCount: FlagCountResponse | undefined;
  keywordCount: GroupCount | undefined;
  topicCount?: TopicCount;
  keywordFilter: string[];
  topicFilter: string[];
  subStep: string;
  isFilterEmpty: boolean;
  dateFilter: { fromDate: string; toDate: string };
  blueprint: BlueprintEx2 | undefined;
  errorBlueprint: boolean | undefined;
  currentType: ReportDetailTypes;
  modalUnlock: React.ReactNode;
  canAddNotes: boolean;
  companyNote?: string;
  showChangeDueDateButton: () => boolean;
  setIsDueDateModalOpen: (open: boolean) => void;
  isDueDateModalOpen: boolean;
  getWorkflowDueDateError: unknown;
  isDueDateErrorModalOpen: boolean;
  setIsDueDateErrorModalOpen: (open: boolean) => void;
  REPORT_DETAILS: { [key in ReportDetailTypes]: JSX.Element };
};

const ReportDetailBase = ({
  reportSummary,
  summaryOnly,
  getDetailTitle,
  useStatsInSummary,
  report,
  isReadOnly,
  reviewIsInProgress,
  canChangeDueDate,
  selectedDate,
  handleDueDateChange,
  applyFilterByType,
  flagCount,
  keywordCount,
  topicCount,
  keywordFilter,
  topicFilter,
  subStep,
  isFilterEmpty,
  dateFilter,
  blueprint,
  errorBlueprint,
  currentType,
  modalUnlock,
  canAddNotes,
  companyNote,
  showChangeDueDateButton,
  setIsDueDateModalOpen,
  isDueDateModalOpen,
  getWorkflowDueDateError,
  isDueDateErrorModalOpen,
  setIsDueDateErrorModalOpen,
  REPORT_DETAILS,
}: Props) => {
  const { enableProfiler } = useFamaFlags();
  // Profiler profile-confirm: fill Layout's h-svh chain and scroll columns
  // independently so Person Info stays visible (no guessed svh offset).
  const stickySidebar =
    enableProfiler && currentType === ReportDetailTypes.PROFILE_CONFIRM;
  return (
    <div
      className={classNames(
        "flex flex-col",
        stickySidebar &&
          "h-full min-h-0 overflow-hidden print:h-auto print:overflow-visible"
      )}
    >
      <PrintReportHeader reportSummary={reportSummary} />
      {!summaryOnly && (
        <div className="flex shrink-0 justify-between items-center pb-6 print:pb-0 print:m-0">
          <div className="flex flex-row items-center">
            {!!blueprint && (
              <PageHeaderTitle>{getDetailTitle()}</PageHeaderTitle>
            )}
            <span
              className={classNames(
                "pl-3 text-4xl leading-normal font-extrabold hidden",
                useStatsInSummary ? "print:hidden" : "print:block"
              )}
            >
              {report?.full_name}
            </span>
            {isReadOnly && reviewIsInProgress() && (
              <div className="p-4 justify-self-end print:hidden">
                <SecurityLockIcon width={"1.5rem"} height={"1.5rem"} />
              </div>
            )}
          </div>

          {canChangeDueDate && (
            <div className="print:hidden flex justify-items-end items-center space-x-2">
              <div className="text-sm leading-4 font-medium text-gray-500">
                Due Date:
              </div>
              <div className="text-sm leading-4 font-medium text-gray-900">
                {reportSummary && (
                  <div className="whitespace-pre flex text-center">
                    <TableReportDateWithFormat
                      date={
                        selectedDate
                          ? selectedDate.toISOString()
                          : reportSummary.due_date
                      }
                      updated={false}
                    />
                  </div>
                )}
              </div>
              {!isReadOnly && showChangeDueDateButton() && (
                <div className="ml-4" data-testid="due-date-change">
                  <Button
                    text="Change"
                    onClick={() => setIsDueDateModalOpen(true)}
                    colorType={ButtonColorTypes.cancel}
                    className="w-19    h-8    max-w-xs max-h-9"
                  />
                </div>
              )}
            </div>
          )}
          <Modal
            open={isDueDateModalOpen}
            onClose={() => setIsDueDateModalOpen(false)}
            title={"Change Due Date"}
            width={"w-full max-w-sm"}
          >
            {reportSummary && (
              <ChangeDueDateContainer
                selectedDate={selectedDate ?? new Date(reportSummary.due_date)}
                createdAt={new Date(reportSummary.created_at)}
                handleDueDateChange={handleDueDateChange}
              />
            )}
          </Modal>
          <Modal
            open={!!(getWorkflowDueDateError && !isDueDateErrorModalOpen)}
            onClose={() => setIsDueDateErrorModalOpen(true)}
            title={"Change Due Date"}
            width={"w-full max-w-lg"}
          >
            Error occured, please try again
          </Modal>
        </div>
      )}
      <div
        className={classNames(
          "flex",
          stickySidebar && "min-h-0 flex-1 print:h-auto print:flex-none"
        )}
      >
        <div
          className={classNames(
            summaryOnly
              ? "w-full"
              : "bg-white p-6 rounded-xl shadow shadow-gray-300 print:p-0 print:shadow-none w-full",
            stickySidebar &&
              "flex min-h-0 flex-1 flex-col overflow-hidden print:h-auto print:flex-none print:overflow-visible"
          )}
        >
          {reviewIsInProgress() && canAddNotes && companyNote && (
            <div className="bg-yellow-100 flex flex-start shrink-0 text-gray-700 rounded-md text-sm leading-5 font-medium mb-4 p-4 items-center">
              <AddNoteIcon />
              <span className="ml-4">{companyNote}</span>
            </div>
          )}
          <div
            className={classNames(
              "grid grid-cols-8 gap-6",
              // Constrain the row track so column overflow-y-auto can scroll
              // independently instead of sizing to content and clipping.
              stickySidebar &&
                "min-h-0 flex-1 grid-rows-1 print:h-auto print:flex-none print:grid-rows-none"
            )}
          >
            <div
              className={classNames(
                "col-span-2 print:hidden",
                stickySidebar &&
                  "min-h-0 h-full overflow-y-auto print:h-auto print:overflow-visible"
              )}
            >
              <ReportSidebar
                blueprint={blueprint}
                errorBlueprint={!!errorBlueprint}
                report={reportSummary}
                isReadOnly={isReadOnly}
                reportDetailType={currentType}
                applyFilterByType={applyFilterByType}
                flagCount={flagCount}
                keywordCount={keywordCount}
                topicCount={topicCount}
                keywordFilter={keywordFilter}
                topicFilter={topicFilter}
                subStep={subStep}
                isFilterEmpty={isFilterEmpty}
                dateFilter={dateFilter}
                summaryOnly={summaryOnly}
              />
            </div>
            <div
              className={classNames(
                "col-span-6 print:col-span-8",
                stickySidebar &&
                  "min-h-0 h-full overflow-y-auto print:h-auto print:overflow-visible"
              )}
            >
              {REPORT_DETAILS[currentType]}
            </div>
          </div>
        </div>
      </div>
      {modalUnlock}
      <ReportTimer reportDetailType={currentType}></ReportTimer>
    </div>
  );
};

export default ReportDetailBase;
